<script>
	import { createEventDispatcher } from "svelte";

	// 공통 이미지 업로드 카드(드롭존).
	//  - imageUrl 없으면: 점선 드롭존(카드 전체 클릭 + 드래그&드롭)
	//  - imageUrl 있으면: 미리보기 + hover 시 교체/삭제 오버레이
	//  - showAi=true 면 카드 아래 보조 'AI 생성' 버튼
	// 이벤트:
	//  - file  : 파일 선택/드롭. detail = { target: { files } } → 기존 onImageFileUpload에 그대로 전달 가능
	//  - remove: 삭제
	//  - ai    : AI 생성 버튼 클릭
	//  - pick  : (pickMode 전용) 라이브러리에서 고르기 요청. 부모가 ImagePicker 모달을 연다
	export let imageUrl = null;
	export let hint = "";
	export let accept = "image/*";
	export let minHeight = "200px";
	export let showAi = false;
	export let aiLabel = "AI로 생성";
	export let emptyText = "클릭하거나 이미지를 끌어다 놓으세요";
	export let objectFit = "contain"; // 'contain' | 'cover'
	// true면 부모 높이를 꽉 채우도록 카드가 늘어남(그리드 셀 등에서 형제 카드와 높이 맞출 때).
	// minHeight는 최소 높이(바닥)로만 작동.
	export let fill = false;
	// true면 로컬 파일 업로드 대신 이미지 자산(에셋) 라이브러리에서 고르는 카드로 동작한다.
	// 파일 input/드래그&드롭을 쓰지 않고 클릭·교체가 모두 'pick' 이벤트로 나간다.
	export let pickMode = false;

	// ── 크기 ──
	// 카드 높이는 minHeight 하나로 결정되고 이미지가 들어와도 그대로다(업로드 전후 레이아웃이 안 튐).
	// 그러려면 미리보기가 카드보다 커지면 안 되므로, 기본 상한을 minHeight에서 안쪽 여백(8px*2)만큼
	// 뺀 값으로 잡는다. 세로로 긴 이미지는 이 상한에 맞춰 축소되고 카드 높이는 minHeight로 고정된다.
	export let previewMaxHeight = null;
	$: previewMax = previewMaxHeight || `calc(${minHeight} - 16px)`;

	const dispatch = createEventDispatcher();

	let inputRef;
	let dragOver = false;

	// pickMode면 로컬 파일 대화상자 대신 부모에게 라이브러리 열기를 요청한다.
	const openPicker = () => {
		if (pickMode) return dispatch("pick");
		if (inputRef) inputRef.click();
	};

	const emitFiles = (files) => {
		if (!files || !files.length) return;
		dispatch("file", { target: { files } });
	};

	const onInputChange = (e) => {
		emitFiles(e.target.files);
		e.target.value = ""; // 같은 파일 재선택 허용
	};

	const onDrop = (e) => {
		if (pickMode) return; // 라이브러리 모드에선 로컬 드롭 업로드를 받지 않는다(업로드는 모달 안에서)
		e.preventDefault();
		dragOver = false;
		if (e.dataTransfer) emitFiles(e.dataTransfer.files);
	};

	const onDragOver = (e) => {
		if (pickMode) return;
		e.preventDefault();
		dragOver = true;
	};

	const onDragLeave = () => (dragOver = false);
</script>

<div class="iu-block" class:fill>
	<div
		class="iu-card"
		class:empty={!imageUrl}
		class:filled={!!imageUrl}
		class:drag={dragOver}
		style="min-height:{minHeight}"
		on:click={() => !imageUrl && openPicker()}
		on:dragover={onDragOver}
		on:dragleave={onDragLeave}
		on:drop={onDrop}
		role="button"
		tabindex="0"
	>
		{#if imageUrl}
			<img class="iu-preview" src={imageUrl} alt="" loading="lazy" style="object-fit:{objectFit};max-height:{previewMax}" />
			<div class="iu-overlay">
				<button type="button" class="iu-ovl-btn" on:click|stopPropagation={openPicker}>{pickMode ? "변경" : "교체"}</button>
				<button type="button" class="iu-ovl-btn danger" on:click|stopPropagation={() => dispatch("remove")}>삭제</button>
			</div>
		{:else}
			<div class="iu-empty">
				<svg class="iu-empty-icon" width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
					<path d="M12 16V6m0 0l-3.5 3.5M12 6l3.5 3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
					<path d="M4 15v2.5A2.5 2.5 0 006.5 20h11a2.5 2.5 0 002.5-2.5V15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
				</svg>
				<div class="iu-empty-text">{emptyText}</div>
				{#if hint}<div class="iu-empty-hint">{hint}</div>{/if}
			</div>
		{/if}
	</div>

	{#if showAi}
		<button type="button" class="iu-ai-btn" on:click={() => dispatch("ai")}>
			<span class="iu-ai-spark">✦</span>{aiLabel}
		</button>
	{/if}

	<input type="file" {accept} bind:this={inputRef} on:change={onInputChange} hidden />
</div>

<style>
	.iu-block {
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
	}

	/* 부모 높이 채우기 — 드롭존 카드가 늘어나고 AI 버튼은 하단 고정 */
	.iu-block.fill {
		height: 100%;
	}

	.iu-block.fill .iu-card {
		flex: 1 1 auto;
	}

	.iu-card {
		position: relative;
		width: 100%;
		border-radius: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-sizing: border-box;
		transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
	}

	.iu-card.empty {
		border: 1.5px dashed hsl(var(--sb-border));
		background-color: hsl(var(--sb-muted) / 0.35);
		cursor: pointer;
	}

	.iu-card.empty:hover {
		border-color: hsl(var(--sb-primary));
		background-color: hsl(var(--sb-primary) / 0.08);
	}

	.iu-card.drag {
		border-color: hsl(var(--sb-primary));
		background-color: hsl(var(--sb-primary) / 0.12);
		box-shadow: 0 0 0 3px hsl(var(--sb-primary) / 0.2);
	}

	.iu-card.filled {
		border: 1px solid hsl(var(--sb-border));
		background-color: hsl(var(--sb-card));
	}

	/* height:100%는 카드에 확정 높이가 없으므로 auto로 풀린다 → 이미지 원래 비율대로,
	   다만 인라인 max-height(previewMax) 안으로 제한된다. */
	.iu-preview {
		width: 100%;
		height: 100%;
		display: block;
	}

	/* 미리보기가 카드 높이를 채우도록 (contain일 때 여백 최소화) */
	.iu-card.filled {
		padding: 8px;
	}

	.iu-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 6px;
		padding: 16px;
		color: hsl(var(--sb-muted-foreground));
		text-align: center;
		pointer-events: none;
	}

	.iu-empty-icon {
		color: hsl(var(--sb-muted-foreground) / 0.7);
	}

	.iu-card.empty:hover .iu-empty-icon,
	.iu-card.drag .iu-empty-icon {
		color: hsl(var(--sb-primary));
	}

	.iu-empty-text {
		font-size: 14px;
		font-weight: 500;
		color: hsl(var(--sb-foreground) / 0.75);
	}

	.iu-empty-hint {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground) / 0.8);
	}

	.iu-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		background: rgba(17, 20, 27, 0.42);
		opacity: 0;
		transition: opacity 0.15s;
	}

	.iu-card.filled:hover .iu-overlay {
		opacity: 1;
	}

	.iu-ovl-btn {
		border: none;
		border-radius: 999px;
		padding: 7px 16px;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
		background: #fff;
		color: #333;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
	}

	.iu-ovl-btn.danger {
		background: hsl(var(--sb-destructive));
		color: #fff;
	}

	.iu-ai-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		width: 100%;
		height: 44px;
		border: 1px solid hsl(var(--sb-primary));
		border-radius: 10px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-primary));
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: background-color 0.15s;
	}

	.iu-ai-btn:hover {
		background: hsl(var(--sb-primary) / 0.08);
	}

	.iu-ai-spark {
		font-size: 13px;
	}
</style>
